import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

import {
    getBookingNotifications,
    markBookingNotificationsSeen,
} from '../../../redux/actions/Booking/Notification/bookingNotificationActions';
import {
    BOOKING_NOTIFICATION_BADGE_MAX,
    BOOKING_NOTIFICATION_PAGE_SIZE,
} from '../../../constants/Booking/bookingNotification.constants';
import {NETWORK_RESERVATIONS} from '../../../constants/appUrls.constants';
import {formatBadgeCount, isBookingNotificationEnabled} from './bookingNotificationHelpers';
import BookingNotificationItem from './BookingNotificationItem';

const SKELETON_ROWS = [0, 1, 2];

export default function BookingNotificationBell() {

    const {t, i18n} = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();
    const rootRef = useRef(null);

    const state = useSelector(props => props.bookingNotificationReducer);
    const {count, items, seen, initialized, fetching, error, latestId} = state;

    const [open, setOpen] = useState(false);
    // Yeni kayit dustugunde zil bir kez sallanir.
    const [ringing, setRinging] = useState(false);
    const lastRungId = useRef(0);

    const badge = formatBadgeCount(count, BOOKING_NOTIFICATION_BADGE_MAX);
    const hasNew = count > 0;

    useEffect(() => {
        if (!hasNew || latestId <= lastRungId.current) return;
        lastRungId.current = latestId;
        setRinging(true);
        const timer = setTimeout(() => setRinging(false), 900);
        return () => clearTimeout(timer);
    }, [latestId, hasNew]);

    const togglePanel = () => setOpen(prev => !prev);

    /**
     * Belgedeki en kritik tuzak: cursor menu ACILINCA ilerletilirse, ayni anda
     * kosan yoklama count:0 dondurur ve liste kullanicinin gozu onunde bosalir.
     * Bu yuzden "gorulmus" isareti yalnizca KAPANISTA atiliyor.
     */
    const closePanel = useCallback(() => {
        setOpen(false);
        dispatch(markBookingNotificationsSeen());
    }, [dispatch]);

    /**
     * Mobilde (<=600px) panel dropdown degil, ekran genisligini kullanan bir
     * yaprak: `position: fixed` + soldan/sagdan 10px. Cana sabitlendiginde
     * 390px'lik ekranda sol kenari ekran disina tasiyordu (-25px).
     *
     * `fixed` akistan ciktigi icin dikey konumu buradan veriyoruz; degeri
     * `--bkn-top` ile CSS'e geciyor. Kaydirma/yeniden boyutlandirmada
     * guncelleniyor ki panel masaustundeki gibi canla birlikte hareket etsin.
     * Bu kirilimda `body` zoom'u yok (zoom yalnizca 960-1600px arasi), bu yuzden
     * getBoundingClientRect degerleri birebir kullanilabiliyor.
     */
    const [panelTop, setPanelTop] = useState(null);

    useEffect(() => {

        if (!open) {
            return undefined;
        }

        const update = () => {
            if (rootRef.current) {
                setPanelTop(rootRef.current.getBoundingClientRect().bottom + 8);
            }
        };

        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);

        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [open]);

    /**
     * Disari tiklama + Escape ile kapanma.
     *
     * MUI Popover kullanmiyoruz: uygulama 960–1600px arasi `body { zoom: 0.8 }`
     * uyguluyor. Popover portal ile body'ye tasindigi ve konumu getBoundingClientRect
     * (zoom'lanmis) degerleriyle hesaplandigi icin panel ikinci kez olceklenip
     * ~289px sola kayiyordu. Panel canin yaninda kalirsa ayni olcekte durur ve
     * her zoom seviyesinde dogru hizalanir.
     */
    useEffect(() => {

        if (!open) {
            return undefined;
        }

        // Dokunmatikte `mousedown` ancak uyumluluk olayi olarak gelir ve bazi
        // durumlarda hic uretilmez; `touchstart` ile birlikte dinliyoruz.
        const onDocumentPointerDown = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) {
                closePanel();
            }
        };

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                closePanel();
            }
        };

        document.addEventListener('mousedown', onDocumentPointerDown);
        document.addEventListener('touchstart', onDocumentPointerDown);
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('mousedown', onDocumentPointerDown);
            document.removeEventListener('touchstart', onDocumentPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open, closePanel]);

    const refresh = (event) => {
        event.stopPropagation();
        event.preventDefault();
        // force: ust uste hata sonrasi kapanan devre kesiciyi kullanici acabilsin.
        dispatch(getBookingNotifications(true));
    };

    // Taze kayitlar ustte, daha once gorulmusler altta.
    const rows = useMemo(() => {
        const fresh = (items || []).map(row => ({booking: row, isNew: true}));
        const old = (seen || []).map(row => ({booking: row, isNew: false}));
        return fresh.concat(old);
    }, [items, seen]);

    // count gercek sayidir, items en fazla 20 satir doner. Ikisi bilerek ayrisir.
    const hiddenCount = count > BOOKING_NOTIFICATION_PAGE_SIZE
        ? count - (items || []).length
        : 0;

    const isLoading = !initialized && fetching;
    const hasError = !!error && rows.length === 0;

    const totalLabel = useMemo(() => {
        try {
            return new Intl.NumberFormat(i18n.language || 'tr-TR').format(count);
        } catch (e) {
            return String(count);
        }
    }, [count, i18n.language]);

    if (!isBookingNotificationEnabled(location.pathname)) {
        return null;
    }

    return (
        <div className="bkn" ref={rootRef}>
            <button
                type="button"
                onClick={togglePanel}
                className={`bkn-trigger${hasNew ? ' has-new' : ''}${ringing ? ' is-ringing' : ''}${open ? ' is-open' : ''}`}
                aria-haspopup="true"
                aria-expanded={open}
                aria-label={
                    hasNew
                        ? t('enw-booking_notification-aria_new', {
                            defaultValue: '{{total}} yeni rezervasyon bildirimi',
                            total: count,
                        })
                        : t('enw-booking_notification-title', 'Yeni Rezervasyonlar')
                }
            >
                <i className={hasNew ? 'fas fa-bell' : 'fal fa-bell'} />
                {
                    badge &&
                    <span className="bkn-trigger__badge">{badge}</span>
                }
            </button>

            {
                open &&
                <div
                    className="bkn-panel__paper"
                    style={panelTop !== null ? {'--bkn-top': `${panelTop}px`} : undefined}
                    role="dialog"
                    aria-label={t('enw-booking_notification-title', 'Yeni Rezervasyonlar')}
                >
                <div className="bkn-panel">

                    <div className="bkn-panel__header">
                        <div className="bkn-panel__heading">
                            <h6 className="bkn-panel__title">
                                {t('enw-booking_notification-title', 'Yeni Rezervasyonlar')}
                            </h6>
                        </div>

                        <div className="bkn-panel__actions">
                            {
                                hasNew &&
                                <span className="bkn-panel__count">{totalLabel}</span>
                            }
                            <button
                                type="button"
                                className={`bkn-panel__refresh${fetching ? ' is-busy' : ''}`}
                                onClick={refresh}
                                aria-label={t('btn-refresh', 'Yenile')}
                                title={t('btn-refresh', 'Yenile')}
                            >
                                <i className="fal fa-sync-alt" />
                            </button>
                        </div>
                    </div>

                    <div className="bkn-panel__body">
                        {
                            isLoading &&
                            SKELETON_ROWS.map(row => (
                                <div className="bkn-skeleton" key={row}>
                                    <span className="bkn-skeleton__avatar" />
                                    <span className="bkn-skeleton__lines">
                                        <span className="bkn-skeleton__line" />
                                        <span className="bkn-skeleton__line is-short" />
                                    </span>
                                </div>
                            ))
                        }

                        {
                            !isLoading && hasError &&
                            <div className="bkn-state bkn-state--error">
                                <span className="bkn-state__icon">
                                    <i className="fal fa-exclamation-triangle" />
                                </span>
                                <p className="bkn-state__title">
                                    {t('enw-booking_notification-error', 'Bildirimler alınamadı.')}
                                </p>
                                <button type="button" className="bkn-state__retry" onClick={refresh}>
                                    {t('btn-try_again', 'Tekrar dene')}
                                </button>
                            </div>
                        }

                        {
                            !isLoading && !hasError && rows.length === 0 &&
                            <div className="bkn-state">
                                <span className="bkn-state__icon">
                                    <i className="fal fa-bell-slash" />
                                </span>
                                <p className="bkn-state__title">
                                    {t('enw-booking_notification-empty', 'Yeni rezervasyon yok.')}
                                </p>
                                <p className="bkn-state__desc">
                                    {t('enw-booking_notification-empty_desc', 'Yeni bir rezervasyon düştüğünde burada göreceksiniz.')}
                                </p>
                            </div>
                        }

                        {
                            !isLoading && rows.length > 0 &&
                            <div className="bkn-list">
                                {
                                    rows.map(row => (
                                        <BookingNotificationItem
                                            key={row.booking.id}
                                            booking={row.booking}
                                            isNew={row.isNew}
                                            onNavigate={closePanel}
                                        />
                                    ))
                                }
                                {
                                    hiddenCount > 0 &&
                                    <p className="bkn-list__more">
                                        {t('enw-booking_notification-more', {
                                            defaultValue: 'En yeni {{shown}} kayıt gösteriliyor · toplam {{total}} yeni rezervasyon',
                                            shown: (items || []).length,
                                            total: totalLabel,
                                        })}
                                    </p>
                                }
                            </div>
                        }
                    </div>

                    <Link to={NETWORK_RESERVATIONS} className="bkn-panel__footer" onClick={closePanel}>
                        {t('enw-booking_notification-see_all', 'Tüm rezervasyonları gör')}
                        <i className="fas fa-arrow-right" />
                    </Link>
                </div>
                </div>
            }
        </div>
    );
}
